import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { Link as RouterLink, useNavigate } from 'react-router'
import { Stack, Button, FormHelperText, Link, Typography } from '@mui/material'
import { OverlayState } from '@/hooks/useOverlay'
import { useGetOrgTypes } from '@/service-library/hooks/org-types'
import {
  useCreateOrganization,
  useGetOrganization,
  useUpdateOrganization,
} from '@/service-library/hooks/organizations'
import queryKeys from '@/service-library/query-keys'
import { OrgType, Organization } from '@/types/organizations'
import { showErrorSnackbar } from '@/utils/snackbars'
import generateUuid from '@/utils/uuid'
import { Dialog, DialogContent, DialogFooter } from '@/components/dialog'
import {
  FormAutocomplete,
  FormCheckbox,
  FormTextField,
  PixyDocsForm,
} from '@/components/forms'

type AddEditOrganizationDialogProps = {
  overlay: OverlayState
  organization?: Organization
  parentOrgId?: string
}

type FormValues = {
  name: string
  code: string
  org_type: OrgType | null
  is_relay_data_provider: boolean
}

export default function AddEditOrganizationDialog({
  overlay,
  organization,
  parentOrgId,
}: AddEditOrganizationDialogProps) {
  const navigate = useNavigate()

  const { queryKey } = useGetOrganization({
    id: organization?.id || '',
    filters: {
      fields__include: 'org_type',
    },
    enabled: false,
  })

  const { orgTypes } = useGetOrgTypes({
    filters: {
      limit: '1000',
    },
    enabled: overlay.isOpen,
  })

  const { createOrganization } = useCreateOrganization({
    sideEffectQueryKeys: [queryKeys.organizations.lists()],
    onError: () => {
      showErrorSnackbar(
        'Unable to create organization. Please try again later.',
      )
    },
    onSuccess: (newOrg) => {
      navigate(`/settings/organizations/${newOrg.id}`)
    },
  })

  const { updateOrganization } = useUpdateOrganization({
    detailQueryKey: queryKey,
    sideEffectQueryKeys: [queryKeys.organizations.lists()],
    onError: () => {
      showErrorSnackbar('Unable to update organization')
    },
  })

  const defaultValues = useMemo(
    () => ({
      name: organization?.name || '',
      code: organization?.code || '',
      org_type: organization?.org_type || null,
      is_relay_data_provider: organization?.is_relay_data_provider || false,
    }),
    [
      organization?.code,
      organization?.is_relay_data_provider,
      organization?.name,
      organization?.org_type,
    ],
  )

  const methods = useForm<FormValues>({
    defaultValues,
  })

  const {
    formState: { isDirty, isValid },
    reset,
  } = methods

  const handleSubmit = (values: FormValues) => {
    values.name = values.name.trim()
    values.code = values.code.trim()

    const orgTypeId = values.org_type ? values.org_type.id : null

    if (!organization) {
      if (parentOrgId) {
        createOrganization({
          ...values,
          id: generateUuid(),
          color: 'blue',
          parent_org_id: parentOrgId,
          org_type_id: orgTypeId,
          status: 'active',
        } as Organization)
      }
    } else {
      const updatedOrganization = {
        ...organization,
        ...values,
        org_type_id: orgTypeId,
      }

      updateOrganization(updatedOrganization)
    }
    overlay.close()
  }

  useEffect(() => {
    reset(defaultValues)
  }, [defaultValues, overlay.isOpen, reset])

  return (
    <Dialog
      maxWidth="sm"
      {...overlay}
      title={`${organization?.name ? 'Edit' : 'New'} Organization`}
    >
      <PixyDocsForm methods={methods} onSubmit={handleSubmit}>
        <DialogContent>
          <Stack spacing={4}>
            <FormTextField
              name="name"
              autoFocus
              label="Name"
              fullWidth
              helperText="Provide a name for the organization. This is what will be shown in the UI."
              required
              useControllerProps={{
                rules: {
                  validate: (value) => value.trim().length > 0,
                },
              }}
            />

            <FormTextField
              name="code"
              label="Identifier"
              fullWidth
              helperText="Unique identifier. This is typically an external ID used to link this organization to an external system, such as a company code or client ID."
            />
            <div>
              <FormAutocomplete
                name="org_type"
                label="Type"
                options={orgTypes}
                isOptionEqualToValue={(option, value) =>
                  option?.id === value.id
                }
                getOptionLabel={(option) => option?.name || ''}
              />
              <FormHelperText sx={{ mx: 1.75, mt: 0.5 }}>
                You can manage organization types{' '}
                <Link
                  component={RouterLink}
                  to="../organization-types"
                  underline="none"
                  target="_blank"
                >
                  here
                </Link>
                .
              </FormHelperText>
            </div>
            <FormCheckbox
              name="is_relay_data_provider"
              label={
                <Stack>
                  <Typography>External Data Provider</Typography>
                  <Typography color="textSecondary" variant="caption">
                    If enabled, the details of this organization will be used to
                    pull and push data from an external system.
                  </Typography>
                </Stack>
              }
              sx={{ mt: -1, ml: -1 }}
              formLabelSx={{
                alignItems: 'flex-start',
                width: 'fit-content',
              }}
            />
          </Stack>
        </DialogContent>

        <DialogFooter>
          <Button variant="text" onClick={overlay.close}>
            Cancel
          </Button>
          <Button type="submit" disabled={!isDirty || !isValid}>
            Save
          </Button>
        </DialogFooter>
      </PixyDocsForm>
    </Dialog>
  )
}
